import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { AlertCircle } from 'lucide-react';
import * as authApi from '@/api/auth.api';
import { ApiError } from '@/api/client';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { AuthShell } from './AuthShell';

/**
 * Página PÚBLICA de canje de invitación (/invite/:token): el invitado pone su
 * nombre y crea su contraseña; al aceptar queda creada la cuenta y se lo lleva
 * al login. Tokens inválidos/expirados muestran el error del backend.
 */
export default function AcceptInvite() {
  const { token = '' } = useParams();
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);

  const accept = useMutation({
    mutationFn: () => authApi.acceptInvitation({ token, password, name: name.trim() || undefined }),
    onSuccess: () => {
      navigate('/login', { replace: true });
    },
    onError: (e) =>
      setError(e instanceof ApiError ? e.message : 'No se pudo completar el registro'),
  });

  function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (password.length < 8) return setError('La contraseña debe tener al menos 8 caracteres');
    if (password !== confirm) return setError('Las contraseñas no coinciden');
    accept.mutate();
  }

  return (
    <AuthShell title="Crear su cuenta" subtitle="Lo invitaron al panel. Elija su contraseña para entrar.">
      <form onSubmit={submit} className="space-y-4" noValidate>
        <Input label="Su nombre" value={name} onChange={(e) => setName(e.target.value)} placeholder="Nombre y apellido" />
        <Input
          label="Contraseña (mínimo 8 caracteres)"
          type="password"
          autoComplete="new-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />
        <Input
          label="Repita la contraseña"
          type="password"
          autoComplete="new-password"
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
        />
        {error && (
          <div className="flex items-center gap-2 rounded-md bg-red-50 px-3 py-2 text-sm text-red-600 dark:bg-red-900/20 dark:text-red-300">
            <AlertCircle className="h-4 w-4 shrink-0" /> {error}
          </div>
        )}
        <Button type="submit" className="w-full" loading={accept.isPending}>
          Crear cuenta
        </Button>
        <p className="text-center text-xs text-slate-400">
          ¿Ya tiene cuenta?{' '}
          <Link to="/login" className="text-acento-texto hover:underline">
            Inicie sesión
          </Link>
        </p>
      </form>
    </AuthShell>
  );
}
